import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import { ChangeEvent, FC, useRef } from 'react';

import type { IVisibleBanner } from '../banners-section';
import { nativeStyles, styles } from './gallery-preview.styles';

interface IGalleryPreviewProps {
  visibleBanners: IVisibleBanner[];
  onAddBanner: (file: File, preview: string) => void;
}

export const GalleryPreview: FC<IGalleryPreviewProps> = ({ onAddBanner, visibleBanners }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getSrc = (idx: number) => visibleBanners[idx]?.url || visibleBanners[idx]?.preview || '';

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const preview = URL.createObjectURL(file);
      onAddBanner(file, preview);
    }

    e.target.value = '';
  };

  return (
    <Box sx={styles.collageWrapper}>
      <input
        ref={fileInputRef}
        accept="image/jpeg, image/png, image/jpg, image/svg"
        hidden
        onChange={handleFileChange}
        type="file"
      />

      <Box sx={styles.colSmallest}>
        <Box sx={styles.imageBox}>
          <Box sx={styles.badgeInfo}>4</Box>
          {getSrc(3) ? (
            <img alt="banner-4" src={getSrc(3)} style={nativeStyles.imageElement} />
          ) : (
            <EmptySlot onClick={handleBoxClick} />
          )}
        </Box>
        <Box sx={styles.imageBox}>
          <Box sx={styles.badgeInfo}>3</Box>
          {getSrc(2) ? (
            <img alt="banner-3" src={getSrc(2)} style={nativeStyles.imageElement} />
          ) : (
            <EmptySlot onClick={handleBoxClick} />
          )}
        </Box>
      </Box>

      <Box sx={styles.colMedium}>
        <Box sx={styles.badgeInfo}>2</Box>
        {getSrc(1) ? (
          <img alt="banner-2" src={getSrc(1)} style={nativeStyles.imageElement} />
        ) : (
          <EmptySlot onClick={handleBoxClick} />
        )}
      </Box>

      <Box sx={styles.colLarge}>
        <Box sx={styles.badgeInfo}>1</Box>
        {getSrc(0) ? (
          <img alt="banner-1" src={getSrc(0)} style={nativeStyles.imageElement} />
        ) : (
          <EmptySlot onClick={handleBoxClick} />
        )}
      </Box>
    </Box>
  );
};

const EmptySlot: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ...(styles.emptyPlaceholder as object),
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.2s, transform 0.2s',
      '&:hover': { opacity: 0.85, transform: 'scale(1.02)' },
    }}
    title="Click to upload image"
  >
    <AddIcon sx={{ color: 'rgba(0, 0, 0, 0.3)', fontSize: 48 }} />
  </Box>
);
